import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import { createQueryKey, invalidateGlobalsQueryKey, FINANCIAL_MODEL_SETTINGS_BASE_KEY } from '@/api/query-keys'
import {
  GrantSetup,
  GrantPeriodResponse,
  TotalsGrantModalResponse,
  UpdateGrantSetup,
  UpdateGrantPeriod,
  GrantAllowedTypes,
  GrantBaseFieldsKeys
} from '../types/grant'
import { PeriodTypeQuery, PeriodTypeView } from '@/types'
import { CAPITAL_STRUCTURE_TOTALS_KEY } from './totals'

// Query keys
const GRANT_BASE_KEY = [`capital-structure`, `grant`]

export const GRANT_SETUP_KEY = createQueryKey({
  baseKey: GRANT_BASE_KEY,
  keys: [`setup`]
})

export const GRANT_DATA_ENTRY_KEY = createQueryKey({
  baseKey: GRANT_BASE_KEY,
  keys: [`data-entry`]
})

export const GRANT_TOTALS_KEY = CAPITAL_STRUCTURE_TOTALS_KEY

// Setup endpoints
const getGrantSetup = async (financial_model_id: string) => {
  const { data } = await axiosInstance.get<GrantSetup>(`/financial-models/${financial_model_id}/capital-structure/grant/setup`)
  return data
}

export const useGetGrantSetup = (financial_model_id: string) =>
  useSuspenseQuery({
    queryKey: GRANT_SETUP_KEY.concat([financial_model_id]),
    queryFn: () => getGrantSetup(financial_model_id)
  })

type UpdateGrantSetupParams = {
  financial_model_id: string
  data: UpdateGrantSetup
}

const updateGrantSetup = async ({ financial_model_id, data }: UpdateGrantSetupParams) => {
  const { data: response } = await axiosInstance.post<GrantSetup>(`/financial-models/${financial_model_id}/capital-structure/grant/setup`, data)
  return response
}

export const useUpdateGrantSetup = ({ financial_model_id }: Pick<UpdateGrantSetupParams, 'financial_model_id'>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateGrantSetup) => updateGrantSetup({ financial_model_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: GRANT_SETUP_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: GRANT_DATA_ENTRY_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: GRANT_TOTALS_KEY.concat([financial_model_id])
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

// Data entry endpoints
type GetGrantDataEntryParams = {
  financial_model_id: string
  type: PeriodTypeQuery
  view: PeriodTypeView
}

const getGrantDataEntry = async ({ financial_model_id, type, view }: GetGrantDataEntryParams) => {
  const { data } = await axiosInstance.get<GrantPeriodResponse>(`/financial-models/${financial_model_id}/capital-structure/grant/data-entry`, {
    params: { type, view }
  })
  return data
}

export const useGetGrantDataEntry = ({ financial_model_id, type, view }: GetGrantDataEntryParams) =>
  useSuspenseQuery({
    queryKey: GRANT_DATA_ENTRY_KEY.concat([financial_model_id, type, view]),
    queryFn: () => getGrantDataEntry({ financial_model_id, type, view })
  })

// Update Grant period data
const updateGrantPeriodData = async (financialModelId: string, payload: UpdateGrantPeriod) => {
  const { data } = await axiosInstance.post(`/financial-models/${financialModelId}/capital-structure/grant/data-entry`, payload)
  return data
}

export const useUpdateGrantPeriodData = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateGrantPeriod) => updateGrantPeriodData(financialModelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: GRANT_DATA_ENTRY_KEY.concat([financialModelId])
      })
      queryClient.resetQueries({
        queryKey: GRANT_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

export const useBulkUpdateGrantPeriodData = (financialModelId: string) =>
  useMutation({
    mutationFn: (payload: UpdateGrantPeriod) => updateGrantPeriodData(financialModelId, payload)
  })

// Totals endpoints
type GetGrantTotalsParams = {
  financial_model_id: string
  type: PeriodTypeQuery
  view: PeriodTypeView
}

const getGrantTotals = async ({ financial_model_id, type, view }: GetGrantTotalsParams) => {
  const { data } = await axiosInstance.get<TotalsGrantModalResponse>(
    `/financial-models/${financial_model_id}/capital-structure/grant/data-entry/totals`,
    { params: { type, view } }
  )
  return data
}

export const useGetGrantTotals = ({ financial_model_id, type, view }: GetGrantTotalsParams) =>
  useSuspenseQuery({
    queryKey: GRANT_TOTALS_KEY.concat([financial_model_id, type, view]),
    queryFn: () => getGrantTotals({ financial_model_id, type, view })
  })

// Column config endpoints
type SwitchGrantColumnTypeParams = {
  financial_model_id: string
  input_type: GrantAllowedTypes
  column_name: GrantBaseFieldsKeys
  month?: number
  year?: number
  product_id?: string | null
  category_id?: string | null
}

const switchGrantColumnType = async (params: SwitchGrantColumnTypeParams) => {
  const { financial_model_id, column_name, ...rest } = params
  const { data } = await axiosInstance.post(`/financial-models/${financial_model_id}/capital-structure/grant/config/${column_name}`, undefined, {
    params: rest
  })
  return data
}

export const useSwitchGrantColumnType = (financial_model_id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: SwitchGrantColumnTypeParams) => switchGrantColumnType(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: GRANT_SETUP_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: GRANT_DATA_ENTRY_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: GRANT_TOTALS_KEY.concat([financial_model_id])
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}
